import React from 'react';
import { Link } from 'react-router-dom';
import ImplantCenterLayout from './ImplantCenterLayout';

const JawboneGrafting = () => {
  return (
    <ImplantCenterLayout
      title="Jawbone Grafting & Sinus Lift Fort Worth TX | Bone Augmentation | Fort Worth Dental Implant Center"
      description="Jawbone grafting and sinus lift procedures in Fort Worth TX. Prepare your jaw for dental implants with expert bone augmentation surgery. FREE consultation. Call (817) 854-4329."
      keywords="jawbone grafting fort worth, sinus lift fort worth tx, bone augmentation, dental bone graft, sinus augmentation, implant bone graft"
    >
      {/* Hero */}
      <section className="relative py-20 bg-gradient-to-br from-gray-900 to-gray-800">
        <div className="absolute inset-0 bg-cover bg-center opacity-30" style={{backgroundImage: "url('/images/services/bone-grafting-new-1.jpg')"}} />
        <div className="relative container mx-auto px-4">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Jawbone Grafting & Sinus Lift in Fort Worth, TX
            </h1>
            <p className="text-xl text-gray-200 mb-8">
              Building a strong foundation for successful dental implants. Expert bone augmentation procedures.
            </p>
            <a href="tel:8178544329" className="bg-teal-500 hover:bg-teal-600 text-white px-8 py-4 rounded-lg text-lg font-bold transition inline-block">
              FREE Consultation: (817) 854-4329
            </a>
          </div>
        </div>
      </section>

      {/* What Is Bone Grafting */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
            <div>
              <h2 className="text-3xl font-bold text-gray-800 mb-6">What Is Jawbone Grafting?</h2>
              <p className="text-gray-700 text-lg leading-relaxed mb-4">
                <strong>Jawbone grafting</strong> (also called bone augmentation) is a surgical procedure that adds bone material to your jaw to create a stable foundation for dental implants.
              </p>
              <p className="text-gray-700 text-lg leading-relaxed mb-4">
                When you lose a tooth, the surrounding bone begins to deteriorate over time. This bone loss can make it impossible to place dental implants without first rebuilding the bone structure.
              </p>
              <p className="text-gray-700 text-lg leading-relaxed">
                Our experienced oral surgeons use advanced techniques and high-quality bone graft materials to restore your jawbone to a level that can successfully support dental implants.
              </p>
            </div>
            <div>
              <img 
                src="/images/services/bone-grafting-new-2.jpg" 
                alt="Jawbone grafting procedure for dental implants Fort Worth TX" 
                className="rounded-xl shadow-xl w-full"
                onError={(e) => { e.target.src = '/images/services/dentist-examines.jpg'; }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* When Is Bone Grafting Needed */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-800 mb-8 text-center">When Is Bone Grafting Needed?</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white p-6 rounded-xl shadow-md">
                <h3 className="text-xl font-bold text-gray-800 mb-3">Long-Term Tooth Loss</h3>
                <p className="text-gray-700">If you've been missing teeth for months or years, significant bone loss has likely occurred.</p>
              </div>
              <div className="bg-white p-6 rounded-xl shadow-md">
                <h3 className="text-xl font-bold text-gray-800 mb-3">Periodontal Disease</h3>
                <p className="text-gray-700">Advanced gum disease destroys bone tissue that supports teeth.</p>
              </div>
              <div className="bg-white p-6 rounded-xl shadow-md">
                <h3 className="text-xl font-bold text-gray-800 mb-3">Tooth Extraction</h3>
                <p className="text-gray-700">Bone loss begins immediately after extraction; grafting preserves the socket.</p>
              </div>
              <div className="bg-white p-6 rounded-xl shadow-md">
                <h3 className="text-xl font-bold text-gray-800 mb-3">Trauma or Injury</h3>
                <p className="text-gray-700">Facial injuries may damage jawbone structure requiring reconstruction.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Sinus Lift Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
            <div className="order-2 lg:order-1">
              <img 
                src="/images/services/sinus-lift-diagram.jpg" 
                alt="Sinus lift procedure diagram Fort Worth dental implant center" 
                className="rounded-xl shadow-xl w-full"
                onError={(e) => { e.target.src = '/images/services/dental-implant-model.jpg'; }}
              />
            </div>
            <div className="order-1 lg:order-2">
              <h2 className="text-3xl font-bold text-gray-800 mb-6">What Is a Sinus Lift?</h2>
              <p className="text-gray-700 text-lg leading-relaxed mb-4">
                A <strong>sinus lift</strong> (or sinus augmentation) is a specialized bone grafting procedure for the upper jaw. It adds bone between your jaw and the maxillary sinuses.
              </p>
              <p className="text-gray-700 text-lg leading-relaxed mb-4">
                This procedure is often necessary when:
              </p>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start"><span className="text-teal-600 mr-2">✓</span> Upper back teeth (molars/premolars) need implants</li>
                <li className="flex items-start"><span className="text-teal-600 mr-2">✓</span> Sinuses are too close to the jaw for implant placement</li>
                <li className="flex items-start"><span className="text-teal-600 mr-2">✓</span> Bone height in the upper jaw is insufficient</li>
                <li className="flex items-start"><span className="text-teal-600 mr-2">✓</span> Teeth have been missing for an extended period</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Bone Graft Sources */}
      <section className="py-16 bg-teal-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-800 mb-6 text-center">Types of Bone Graft Material</h2>
            <div className="space-y-4">
              <div className="bg-white p-6 rounded-xl">
                <h3 className="text-xl font-bold text-gray-800 mb-2">Autograft (Your Own Bone)</h3>
                <p className="text-gray-700">Bone harvested from another area of your body, typically the chin or hip. Considered the gold standard as it contains living cells.</p>
              </div>
              <div className="bg-white p-6 rounded-xl">
                <h3 className="text-xl font-bold text-gray-800 mb-2">Allograft (Donor Bone)</h3>
                <p className="text-gray-700">Processed bone from a tissue bank. Safe, effective, and eliminates the need for a second surgical site.</p>
              </div>
              <div className="bg-white p-6 rounded-xl">
                <h3 className="text-xl font-bold text-gray-800 mb-2">Xenograft (Animal Bone)</h3>
                <p className="text-gray-700">Specially processed bone from bovine sources. Provides an excellent scaffold for new bone growth.</p>
              </div>
              <div className="bg-white p-6 rounded-xl">
                <h3 className="text-xl font-bold text-gray-800 mb-2">Synthetic Bone Substitutes</h3>
                <p className="text-gray-700">Lab-created materials that mimic natural bone structure. Biocompatible and readily available.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-teal-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Need Bone Grafting for Dental Implants?</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Our expert team will evaluate your jawbone and create a personalized treatment plan.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <a href="tel:8178544329" className="bg-white text-teal-600 px-8 py-4 rounded-lg text-lg font-bold hover:bg-gray-100 transition">
              Call (817) 854-4329
            </a>
            <Link to="/implant-center#consultation-form" className="border-2 border-white text-white px-8 py-4 rounded-lg text-lg font-bold hover:bg-teal-500 transition">
              Request FREE Consultation
            </Link>
          </div>
        </div>
      </section>
    </ImplantCenterLayout>
  );
};

export default JawboneGrafting;
